/* Shown after a point lands on a day, whether by drag or by "Add here". It asks how the stop
   recurs, which is the one thing the gesture cannot express:
     every → the base weekly series for that weekday
     nth   → only the Nth occurrence of that weekday each month
     date  → only that exact calendar date (one-off)

   antd `<Modal>` per docs/design/spec/02-components.md, which also brings the mask, the focus trap,
   focus return and Escape — the hand-rolled backdrop this replaced had none of them. */

import React from "react";
import { Button, Flex, Modal, Typography } from "antd";
import { CalendarOutlined, PushpinOutlined, RetweetOutlined, SyncOutlined } from "@ant-design/icons";

import { ROUTE_CALENDAR } from "../../../config/lang";
import { fill, translate } from "../../../util/util";
import { dayMonthLabel, localeFor, nthOfMonth, weekdayMon0 } from "./dateUtils";
import { biweeklyNths, isWeekKey } from "./routeModel";
import { ukBiweekly, ukEvery, ukNth } from "./ukGrammar";

const ORDINAL_KEY = { 1: "ord_first", 2: "ord_second", 3: "ord_third", 4: "ord_fourth", 5: "ord_fifth" };

/* "Every Monday" / "Every 2nd Monday" / "Only on 14 September" for `date`, in `lang`.
   Ukrainian needs genitive + gender agreement, which a flat template cannot express; those two
   phrases are built explicitly. Other languages use the {day}/{ord} template. */
const scopeLabels = (date, lang) => {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const wd = weekdayMon0(date);
  const localeWeekday = new Intl.DateTimeFormat(localeFor(lang), { weekday: "long" }).format(date);
  const nth = nthOfMonth(date);
  return {
    wd,
    localeWeekday,
    every: lang === "uk" ? ukEvery(wd) : fill(t("scope_every"), { day: localeWeekday }),
    nth: lang === "uk" ? ukNth(wd, nth) : fill(t("scope_nth"), { ord: t(ORDINAL_KEY[nth]), day: localeWeekday }),
    date: fill(t("scope_date"), { date: dayMonthLabel(date, lang) }),
  };
};

/** `titleKey`: "scope_title" when adding a point, "move_scope_title" when moving a stop here. */
export default function ScopeMenu({ date, point, lang, titleKey = "scope_title", onChoose, onCancel }) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const { wd, localeWeekday, every: everyLabel, nth: nthLabel, date: dateLabel } = scopeLabels(date, lang);

  /* "Once every two weeks" = the 1st+3rd or 2nd+4th slot pair (routeModel.biweeklyNths); a 5th
     occurrence pairs with nothing, so the option simply isn't offered there. */
  const pair = biweeklyNths(date);
  const biweeklyLabel = pair
    ? lang === "uk"
      ? ukBiweekly(wd, pair)
      : fill(t("scope_biweekly"), { ordA: t(ORDINAL_KEY[pair[0]]), ordB: t(ORDINAL_KEY[pair[1]]), day: localeWeekday })
    : null;

  const options = [
    { type: "every", label: everyLabel, icon: <RetweetOutlined /> },
    { type: "nth", label: nthLabel, icon: <CalendarOutlined /> },
    ...(biweeklyLabel ? [{ type: "biweekly", label: biweeklyLabel, icon: <SyncOutlined /> }] : []),
    { type: "date", label: dateLabel, icon: <PushpinOutlined /> },
  ];

  return (
    <Modal
      open
      rootClassName="rc-scope"
      title={t(titleKey)}
      /* The three options ARE the actions; a footer of OK/Cancel would leave the primary action
         ambiguous. Cancel stays available as the modal's own close and Escape. */
      footer={null}
      width={440}
      centered
      onCancel={onCancel}>
      <Typography.Text type="secondary" className="rc-scope__point">
        {point?.name}
      </Typography.Text>
      <Flex vertical gap="small" className="rc-scope__opts">
        {options.map(option => (
          <Button key={option.type} className="rc-scope__opt" size="large" block icon={option.icon} onClick={() => onChoose(option.type)}>
            {option.label}
          </Button>
        ))}
      </Flex>
    </Modal>
  );
}

/* Removing a RECURRING stop asks how far the removal reaches — the × used to drop it from every
   matching weekday, when the supervisor meant one date (PICSELL-1626 #9). A one-off stop has only
   its own date, so it never reaches here. */
export function RemoveScopeMenu({ date, stop, lang, onChoose, onCancel }) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const labels = scopeLabels(date, lang);
  const options = [
    { type: "date", label: labels.date, icon: <PushpinOutlined /> },
    isWeekKey(stop.layerKey)
      ? { type: "series", label: labels.nth, icon: <CalendarOutlined /> }
      : { type: "series", label: labels.every, icon: <RetweetOutlined /> },
  ];
  return (
    <Modal open rootClassName="rc-scope" title={t("remove_scope_title")} footer={null} width={440} centered onCancel={onCancel}>
      <Typography.Text type="secondary" className="rc-scope__point">
        {stop.point?.name}
      </Typography.Text>
      <Flex vertical gap="small" className="rc-scope__opts">
        {options.map(option => (
          <Button key={option.type} className="rc-scope__opt" size="large" block icon={option.icon} onClick={() => onChoose(option.type)}>
            {option.label}
          </Button>
        ))}
      </Flex>
    </Modal>
  );
}
